import { useAccount } from "wagmi";

import { CADC } from "@/config/tokens";
import { formatToken } from "@/lib/format";
import { useTokenBalance } from "@/hooks/use-token-balance";

type Props = {
  readonly value: string;
  readonly onChange: (v: string) => void;
  readonly error?: string | null;
};

const sanitise = (raw: string): string => {
  const cleaned = raw.replace(/[^0-9.]/g, "");
  const parts = cleaned.split(".");
  if (parts.length <= 1) return cleaned;
  return `${parts[0]}.${parts.slice(1).join("")}`;
};

export const PrincipalInput = ({ value, onChange, error }: Props) => {
  const { address: account } = useAccount();
  const { balance, isLoading, error: balanceError } = useTokenBalance(
    CADC,
    account,
  );

  if (balanceError) console.error("[balance] CADC fetch failed:", balanceError);

  const balanceLabel = balanceError
    ? "Balance · err"
    : isLoading
      ? "Balance · …"
      : balance
        ? `Balance · ${formatToken(balance.raw, CADC.decimals, 4)} ${CADC.symbol}`
        : `Balance · 0.0000 ${CADC.symbol}`;

  return (
    <label className="block group">
      <div className="flex items-baseline justify-between mb-3">
        <h3>Principal</h3>
        {balance && balance.raw > 0n ? (
          <button
            type="button"
            onClick={() =>
              onChange(sanitise(formatToken(balance.raw, CADC.decimals, 6)))
            }
            className="font-mono text-[10px] tracking-[0.16em] uppercase transition-opacity hover:opacity-70"
            style={{ color: "var(--ink-4)" }}
          >
            {balanceLabel}
          </button>
        ) : (
          <span
            className="font-mono text-[10px] tracking-[0.16em] uppercase"
            style={{ color: "var(--ink-4)" }}
          >
            {balanceLabel}
          </span>
        )}
      </div>

      <div
        className="flex items-center gap-4 pl-5 pr-6 py-4 md:py-3 rounded-[var(--radius-md)] surface-strong transition-all"
        style={{
          borderWidth: 1,
          borderStyle: "solid",
          borderColor: error ? "var(--accent-rose)" : "var(--line-2)",
        }}
      >
        <input
          inputMode="decimal"
          placeholder="0.00"
          value={value}
          onChange={(e) => onChange(sanitise(e.target.value))}
          className="flex-1 min-w-0 bg-transparent border-0 outline-none focus:outline-none focus-visible:outline-none font-serif text-[23px] md:text-[30px] tabular"
          style={{
            fontWeight: 300,
            letterSpacing: "-0.02em",
            color: "var(--ink-1)",
          }}
        />
        <div className="flex items-center gap-2 pl-4 border-l hairline shrink-0">
          <img
            src="/cadc.svg"
            alt=""
            aria-hidden
            className="w-7 h-7 rounded-full"
          />
          <span
            className="font-sans text-[14px] tracking-[0.04em]"
            style={{ color: "var(--ink-1)", fontWeight: 500 }}
          >
            {CADC.symbol}
          </span>
        </div>
      </div>
      {error && (
        <p
          className="mt-2 font-mono text-[11px] tracking-[0.04em]"
          style={{ color: "var(--accent-rose)" }}
        >
          {error}
        </p>
      )}
    </label>
  );
};
